import { useCallback } from "react";
// テストのためにわざと react-redux から useSelector を import している
// eslint-disable-next-line no-restricted-imports
import { useSelector } from "react-redux";

import type { MyEventTicketData, MyProductData } from "@spwn/types";
import type { IssuedTicket } from "@spwn/types/firebase/firestore";
import type { Store } from "store";

export const useRelatedIssuedTicketAndGoods = () => {
  const issuedTicketStates = useSelector(
    (state: Store) => state.ticket.issuedTicketStates
  );

  const getRelatedIssuedTicket = useCallback(
    (
      eventId: string,
      ticketId: string,
      ticketIdx: number
    ): IssuedTicket | null => {
      if (
        // eslint-disable-next-line no-prototype-builtins
        issuedTicketStates.hasOwnProperty(eventId) &&
        ticketIdx !== null &&
        ticketIdx !== undefined &&
        issuedTicketStates[eventId]
      ) {
        const relatedTicket = Object.entries(issuedTicketStates[eventId])
          .filter(([_ticketNum, ticketState]) => {
            return ticketState.ticketId === ticketId && !ticketState.isGoods;
          })
          .sort(
            ([a_ticketNum, _a_ticketState], [b_ticketNum, _b_ticketState]) => {
              const a = a_ticketNum.toString().toLowerCase();
              const b = b_ticketNum.toString().toLowerCase();
              if (a < b) {
                return -1;
              } else if (a > b) {
                return 1;
              }
              return 0;
            }
          );
        if (relatedTicket.length === 0 || relatedTicket.length < ticketIdx + 1)
          return null;
        else if (relatedTicket[ticketIdx]) {
          const [ticketNum, ticketState] = relatedTicket[ticketIdx];
          ticketState["ticketNum"] = ticketNum;
          return ticketState as unknown as IssuedTicket;
        }
        return null;
      } else {
        return null;
      }
    },
    [issuedTicketStates]
  );

  const getRelatedIssuedGoods = useCallback(
    (eventId: string, orderId: number): IssuedTicket | null => {
      const issuedTicket = issuedTicketStates[eventId];
      if (!issuedTicket) {
        return null;
      }
      const relatedTicket = Object.entries(issuedTicket).filter(
        ([_ticketNum, ticketState]) => {
          return ticketState.orderId === undefined
            ? ticketState.isGoods
            : ticketState.orderId === orderId && ticketState.isGoods;
        }
      );
      if (relatedTicket.length === 0) {
        return null;
      }
      // グッズ引き換えはグッズの個数に関わらず一つ
      // @ts-expect-error TS2461
      const [[ticketNum, ticketState]] = relatedTicket;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      ticketState["ticketNum"] = ticketNum;
      return ticketState as unknown as IssuedTicket;
    },
    [issuedTicketStates]
  );

  return { getRelatedIssuedTicket, getRelatedIssuedGoods };
};

// 自分でアプグレ元チケットとアプグレ先チケットを両方所持しているかどうか
export const isBaseTicketFunc = ({
  userId,
  eventId,
  product,
  ticketIdxCounter,
  reverseUpgradeTicketMap,
  myEventTickets,
  getRelatedIssuedTicket,
}: {
  userId: string;
  eventId: string | null;
  product: MyProductData;
  ticketIdxCounter: Record<string, number>;
  reverseUpgradeTicketMap: Record<string, string[]>;
  myEventTickets: MyEventTicketData["tickets"];
  getRelatedIssuedTicket: (
    eventId: string,
    ticketId: string,
    ticketIdx: number
  ) => IssuedTicket | null;
}) => {
  if (!eventId) return false;

  // allowSelfTnumGenerationがfalseの場合はissuedTicketが存在しない可能性があるため、共有チケット周りのチェックを飛ばす
  if (product.allowSelfTnumGeneration) {
    const relatedTicket = getRelatedIssuedTicket(
      eventId,
      product.productId,
      // @ts-expect-error TS7053
      ticketIdxCounter[product.productId]
    );
    if (relatedTicket?.owner !== userId) {
      return false;
    }
  }

  const upgradeTargetTicketId = reverseUpgradeTicketMap[product.productId]?.[0];
  if (!upgradeTargetTicketId) {
    return false;
  }
  const upgradeTargetTicket = myEventTickets.find(
    (t) => t.productId === upgradeTargetTicketId
  );
  if (!upgradeTargetTicket) {
    return false;
  }

  // allowSelfTnumGenerationがfalseの場合はissuedTicketが存在しない可能性があるため、共有チケット周りのチェックを飛ばす
  if (product.allowSelfTnumGeneration) {
    const upgradeTargetTicketRelatedTicket = getRelatedIssuedTicket(
      eventId,
      upgradeTargetTicket.productId,
      // @ts-expect-error TS7053
      ticketIdxCounter[product.productId]
    );
    if (upgradeTargetTicketRelatedTicket?.owner !== userId) {
      return false;
    }
  }

  // チケットのカテゴリが同じかどうかチェック（7,8は配信チケット）
  return (
    [7, 8].includes(product.class as never) ===
    [7, 8].includes(upgradeTargetTicket.class as never)
  );
};
